import { useEffect, useMemo, useRef, useState } from "react";

import config from "@/config";
import useNavigateToArtwork from "@/hooks/useNavigateToArtwork";
import fetchWrapper from "@/services/fetchWrapper.service";
import { HOME_AUTO_INITIAL_ARTWORK_LIMIT } from "@/shared/constants/homeCarousel";
import HomeHeader from "@/shared-components/home/components/HomeHeader";
import Loader from "@/shared-components/loader/Loader";
import StaticSeoHead from "@/shared-components/seo/StaticSeoHead";
import Text from "@/shared-components/text/components/Text";
import AppNavigationPage from "@/shared-components/wrappers/components/AppNavigationPage";

import VirtualList from "../artworks/components/VirtualList";

import "./index.css";

const RANDOM_ENDPOINT = `${config.apis.api.url}/home/links/randomized/artworks`;
const RANDOM_BATCH_LIMIT = 32;
const INITIAL_TARGET_COUNT = Math.max(HOME_AUTO_INITIAL_ARTWORK_LIMIT * 4, RANDOM_BATCH_LIMIT);
const MAX_INITIAL_BATCHES = 10;
const MAX_LOAD_MORE_BATCHES = 3;

function isLimitedEditionArtwork(artwork) {
  return Boolean(
    artwork?.artwork_has_limited_editions
    || artwork?.artwork_about?.artwork_has_limited_editions
    || artwork?.artwork_limited_editions
    || artwork?.artwork_about?.artwork_limited_editions
    || artwork?.artwork_limited_edition
    || artwork?.artwork_about?.artwork_limited_edition
  );
}

function getArtworkKey(artwork) {
  return artwork?._id || artwork?.id || artwork?.slug || null;
}

function mergeUniqueArtworks(current = [], incoming = []) {
  const next = [...current];
  const seen = new Set(
    current
      .map(getArtworkKey)
      .filter(Boolean)
      .map((value) => String(value))
  );

  incoming.forEach((artwork) => {
    const key = getArtworkKey(artwork);
    if (!key) {
      return;
    }

    const normalizedKey = String(key);
    if (seen.has(normalizedKey)) {
      return;
    }

    seen.add(normalizedKey);
    next.push(artwork);
  });

  return next;
}

function filterLimitedEditions(items = []) {
  return items.filter(isLimitedEditionArtwork);
}

async function fetchRandomLimitedEditionBatch() {
  const { response, data } = await fetchWrapper.get(
    `${RANDOM_ENDPOINT}?artwork_limit=${RANDOM_BATCH_LIMIT}`,
    { optionalToken: true }
  );

  if (!response.ok || !Array.isArray(data)) {
    return [];
  }

  return filterLimitedEditions(data);
}

export default function HomeLimitedEditionsPage() {
  const [state, setState] = useState({
    loading: true,
    loadingMore: false,
    data: [],
    error: null,
    exhausted: false,
  });
  const requestIdRef = useRef(0);

  useEffect(() => {
    let cancelled = false;
    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;

    const loadInitialData = async () => {
      setState((current) => ({
        ...current,
        loading: true,
        error: null,
      }));

      try {
        let batchCount = 0;
        let pool = [];

        while (batchCount < MAX_INITIAL_BATCHES && pool.length < INITIAL_TARGET_COUNT) {
          const batch = await fetchRandomLimitedEditionBatch();
          if (cancelled || requestIdRef.current !== requestId) {
            return;
          }

          if (batch.length === 0) {
            batchCount += 1;
            continue;
          }

          pool = mergeUniqueArtworks(pool, batch);
          batchCount += 1;

          if (batch.length < RANDOM_BATCH_LIMIT) {
            break;
          }
        }

        if (cancelled || requestIdRef.current !== requestId) {
          return;
        }

        setState({
          loading: false,
          loadingMore: false,
          data: pool,
          error: pool.length === 0 ? "No limited editions available at the moment." : null,
          exhausted: pool.length === 0,
        });
      } catch (error) {
        if (cancelled || requestIdRef.current !== requestId) {
          return;
        }

        setState({
          loading: false,
          loadingMore: false,
          data: [],
          error: error?.message || "Unable to load limited editions at the moment.",
          exhausted: true,
        });
      }
    };

    void loadInitialData();

    return () => {
      cancelled = true;
    };
  }, []);

  const items = useMemo(
    () => ({
      data: state.data,
      totalDocs: state.exhausted ? state.data.length : state.data.length + 1,
    }),
    [state.data, state.exhausted]
  );

  const navigateToArtwork = useNavigateToArtwork(
    "home_limited_editions_page",
    items,
    `${RANDOM_ENDPOINT}?artwork_limit=${RANDOM_BATCH_LIMIT}`,
    { type: "home-artworks", label: "Limited Editions" }
  );

  const loadMoreData = async () => {
    if (state.loading || state.loadingMore || state.exhausted) {
      return;
    }

    const requestId = requestIdRef.current;
    setState((current) => ({ ...current, loadingMore: true, error: null }));

    try {
      let attempts = 0;
      let nextData = state.data;

      while (attempts < MAX_LOAD_MORE_BATCHES) {
        const batch = await fetchRandomLimitedEditionBatch();
        if (requestIdRef.current !== requestId) {
          return;
        }

        const merged = mergeUniqueArtworks(nextData, batch);
        if (merged.length > nextData.length) {
          nextData = merged;
          break;
        }

        attempts += 1;
      }

      setState((current) => ({
        ...current,
        loading: false,
        loadingMore: false,
        data: nextData,
        exhausted: nextData.length === current.data.length,
        error: nextData.length === current.data.length
          ? "No more limited editions available at the moment."
          : null,
      }));
    } catch (error) {
      if (requestIdRef.current !== requestId) {
        return;
      }

      setState((current) => ({
        ...current,
        loading: false,
        loadingMore: false,
        error: error?.message || "Unable to load more limited editions at the moment.",
      }));
    }
  };

  return (
    <>
      <StaticSeoHead
        title="Limited Editions | Suarte"
        description="Discover limited edition artworks curated on Suarte."
        canonicalPath="/limited-editions"
        pageType="CollectionPage"
        breadcrumbItems={[
          { name: 'Home', url: 'https://suarte.art/' },
          { name: 'Limited Editions', url: 'https://suarte.art/limited-editions' },
        ]}
      />
      <AppNavigationPage>
        <div className="limited-editions__page">
          <HomeHeader
            onInput={() => {}}
            value=""
            text="Limited Editions"
            type="artworks"
            placeholder=""
            hideSearch
          />

          {state.loading ? (
            <Loader />
          ) : items.data.length === 0 ? (
            <Text className="limited-editions__empty-text font-size-medium" paragraph>
              {state.error || "No limited editions available at the moment."}
            </Text>
          ) : (
            <VirtualList
              items={items}
              onLoadMore={loadMoreData}
              navigateToArtwork={navigateToArtwork}
            />
          )}
        </div>
      </AppNavigationPage>
    </>
  );
}
